import { useCallback } from 'react'

import { saveAs } from 'file-saver'

import { useExportAppTemplate } from 'data/templates/appTemplates'
import { AppTemplate } from 'features/templates/types'
import { usePatchedData } from 'utils/usePatchedData'

import { useToast } from 'ui/components/Toast'

import { useGetAppTemplateFromStack } from './useGetAppTemplateFromStack'

type ExportAppTemplateModalState = {
    stack: StackDto | null
    onClose: () => void
}

export function useExportAppTemplateModalState({ stack, onClose }: ExportAppTemplateModalState) {
    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose()
        }
    }

    const { template: appTemplate, isLoading } = useGetAppTemplateFromStack(stack!)
    const { patchedData, applyPatch } = usePatchedData<AppTemplate>(appTemplate)

    const onInteractOutside = useCallback((e: Event) => {
        const el = e.target as HTMLElement | null
        if (!el) return

        if (el.closest('.ag-custom-component-popup')) {
            e.preventDefault()
        }
    }, [])

    const toast = useToast()

    const { mutateAsync: exportAppTemplate } = useExportAppTemplate()
    const onExport = useCallback(async () => {
        if (!stack?._sid) return

        try {
            // eslint-disable-next-line unused-imports/no-unused-vars
            const { id, version, bundle_data, ...exportTemplatePayload } = patchedData

            const template = await exportAppTemplate({
                stackSid: stack._sid,
                template: exportTemplatePayload,
            })

            // Save the template to a file, so the user can upload it to the template library.
            const templateBlob = new Blob([JSON.stringify(template)], {
                type: 'application/json',
            })
            saveAs(templateBlob, `${template.id}.json`)

            toast({
                type: 'success',
                startIcon: {
                    name: 'CheckCircle',
                },
                title: 'Template exported successfully',
            })

            onClose()
        } catch (err) {
            console.error(err)
            toast({
                type: 'error',
                title: 'There was a problem exporting the template',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        }
    }, [exportAppTemplate, onClose, patchedData, stack?._sid, toast])

    return {
        onOpenChange,
        appTemplate: patchedData,
        onChangeAppTemplate: applyPatch,
        isLoading,
        onInteractOutside,
        onExport,
    }
}
